import React from 'react';

const NAV_SECTIONS = [
  { label: 'Overview',    ids: ['dashboard', 'device-map'] },
  { label: 'Stock',       ids: ['stock', 'qc', 'consumables'] },
  { label: 'Operations',  ids: ['locations', 'log'] },
  // Deployment section retained for the Deployment role, whose cut-down nav is
  // the only caller that still passes 'shifts'. Hidden for everyone else via
  // HIDDEN_NAV_IDS in App.js; the section self-hides when no items match.
  { label: 'Deployment',  ids: ['shifts', 'flagged'] },
  { label: 'Quality',     ids: ['maintenance', 'rma', 'components'] },
  { label: 'Systems',     ids: ['history', 'settings'] },
];

function Sidebar({
  nav,
  activeView,
  onNavigate,
  collapsed,
  onCollapse,
  user,
  userRole,
  draftViews = [],
  pendingDispatchCount = 0,
  rejectedDispatchCount = 0,
  flaggedAllocCount = 0,
}) {
  const CollapseIcon = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {collapsed ? (
        <>
          <polyline points="13 17 18 12 13 7" />
          <polyline points="6 17 11 12 6 7" />
        </>
      ) : (
        <>
          <polyline points="11 17 6 12 11 7" />
          <polyline points="18 17 13 12 18 7" />
        </>
      )}
    </svg>
  );

  return (
    <div className={`sidebar${collapsed ? ' collapsed' : ''}`}>
      {/* Header: logo + brand */}
      <div className="sidebar-logo" style={{ borderBottom: '2px solid var(--brand-default)' }}>
        <svg width="26" height="22" viewBox="0 0 46 38" fill="none" style={{ flexShrink: 0 }}>
          <path fillRule="evenodd" clipRule="evenodd" d="M0 0C6.927 15.104 40.419 9.326 45.925 26.938C44.317.57 6.584 9.43 0 0Z" fill="#F1582B"/>
          <path fillRule="evenodd" clipRule="evenodd" d="M22.992 14.634c-1.316.344-2.492 1.077-3.37 2.098-.877 1.022-1.413 2.283-1.536 3.613-.122 1.33.175 2.665.852 3.824.676 1.159 1.699 2.087 2.931 2.657 1.231.57 2.612.756 3.954.53 1.343-.225 2.583-.848 3.552-1.786.97-.939 1.622-2.146 1.869-3.46.246-1.313.075-2.67-.49-3.884-.545.909-1.418 1.586-2.446 1.896-1.028.311-2.137.234-3.11-.217-.977-.45-1.743-1.24-2.151-2.216-.409-.976-.43-2.066-.06-3.055Z" fill="#fff"/>
          <path fillRule="evenodd" clipRule="evenodd" d="M37.454 21.8l-.849-.47-.229-.117c-.047 1.77-.514 3.503-1.365 5.064-.85 1.561-2.06 2.906-3.534 3.928-1.474 1.022-3.17 1.693-4.955 1.959-1.784.265-3.607.119-5.324-.427-1.717-.546-3.28-1.478-4.565-2.72-1.286-1.243-2.258-2.762-2.839-4.438-.58-1.675-.754-3.461-.508-5.214.247-1.754.907-3.427 1.928-4.886-1.9-.574-3.737-1.194-5.455-1.901C8.544 14.688 7.807 17.028 7.595 19.44c-.21 2.413.11 4.842.938 7.124.829 2.281 2.148 4.362 3.867 6.1 1.72 1.74 3.8 3.096 6.1 3.978 2.3.882 4.766 1.271 7.232 1.138 2.466-.133 4.875-.783 7.062-1.907 2.188-1.124 4.104-2.696 5.619-4.608 1.514-1.913 2.593-4.122 3.162-6.479-1.241-1.159-2.625-2.162-4.12-2.986Z" fill="#fff"/>
        </svg>
        {!collapsed && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, overflow: 'hidden' }}>
            <span style={{ fontWeight: 800, fontSize: 16, letterSpacing: 0.3, color: '#fff', whiteSpace: 'nowrap' }}>OWL</span>
            <span style={{ fontWeight: 300, fontSize: 16, color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap' }}>Bench</span>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="sidebar-nav">
        {NAV_SECTIONS.map(section => {
          const items = nav.filter(n => section.ids.includes(n.id));
          if (!items.length) return null;
          return (
            <div key={section.label} className="sidebar-section">
              {!collapsed && <div className="section-label">{section.label}</div>}
              {collapsed && <div style={{ height: 10 }} />}
              {items.map(n => {
                const active = activeView === n.id;
                const hasDraft = draftViews.includes(n.id);
                const hasDispatchAuth = n.id === 'dispatch' && pendingDispatchCount > 0;
                const hasDispatchRej = n.id === 'dispatch' && !hasDispatchAuth && rejectedDispatchCount > 0;
                const hasFlagged = n.id === 'flagged' && flaggedAllocCount > 0;
                // RMA deliberately carries no sidebar badge: overdue RMAs are already
                // surfaced by the notification bell and the queue's own Overdue tab, and a
                // permanent count next to the nav item read as an unclearable alert.
                return (
                  <button
                    key={n.id}
                    className={`nav-item${active ? ' active' : ''}`}
                    onClick={() => onNavigate(n.id)}
                    title={collapsed ? n.l : undefined}
                    style={{ justifyContent: collapsed ? 'center' : 'flex-start' }}
                  >
                    {/* Active left bar */}
                    {active && (
                      <span style={{
                        position: 'absolute',
                        left: 0,
                        top: '20%',
                        bottom: '20%',
                        width: 3,
                        borderRadius: '0 3px 3px 0',
                        background: 'var(--brand-default)',
                      }} />
                    )}
                    <span className="nav-icon" style={{ position: 'relative', flexShrink: 0 }}>
                      {n.i}
                      {(hasDraft || hasDispatchAuth || hasDispatchRej || hasFlagged) && (
                        <span style={{
                          position: 'absolute', top: -3, right: -3,
                          width: 6, height: 6, borderRadius: '50%',
                          background: hasDraft
                            ? 'var(--warning-text)'
                            : hasDispatchAuth
                              ? 'var(--brand-default)'
                              : 'var(--danger-text)',
                          border: '1.5px solid var(--surface-default)',
                        }} />
                      )}
                    </span>
                    {!collapsed && (
                      <>
                        <span style={{ flex: 1 }}>{n.l}</span>
                        {hasDraft && (
                          <span className="pill pill-warning" style={{ fontSize: 9, padding: '1px 5px' }}>
                            DRAFT
                          </span>
                        )}
                        {hasDispatchAuth && (
                          <span className="pill pill-brand" style={{ fontSize: 9, padding: '1px 5px' }}>
                            {pendingDispatchCount}
                          </span>
                        )}
                        {hasDispatchRej && (
                          <span className="pill pill-danger" style={{ fontSize: 9, padding: '1px 5px' }}>
                            REJ
                          </span>
                        )}
                        {hasFlagged && (
                          <span className="pill pill-danger" style={{ fontSize: 9, padding: '1px 5px' }}>
                            {flaggedAllocCount}
                          </span>
                        )}
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Bottom: collapse button + version */}
      <div className="sidebar-footer" style={{ display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'space-between' }}>
        {!collapsed && (
          <span style={{ fontSize: 10, color: 'var(--text-ghost)', letterSpacing: 0.5, fontFamily: 'var(--font-mono)' }}>
            OWL BENCH v1.1.1
          </span>
        )}
        <button
          type="button"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          onClick={onCollapse}
          className="btn btn-ghost"
          style={{ padding: '5px 6px', minWidth: 0 }}
        >
          <CollapseIcon />
        </button>
      </div>
    </div>
  );
}

export default Sidebar;
